/** Balances, the full-size strip, and the settle-up plan (DESIGN.md §12.6). */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { BalanceStrip } from "../../components/layout/BalanceStrip";
import { Button } from "../../components/ui/button";
import { PageScroll } from "../../components/layout/PageScroll";
import { Spinner } from "../../components/ui/spinner";
import { useBalances, useGroup, useGroupId } from "../../components/layout/AppShell";
import { formatMoney, formatSigned, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";

export function BalancesPage() {
  const groupId = useGroupId();
  const navigate = useNavigate();
  const group = useGroup(groupId);
  const balances = useBalances(groupId);
  const [copied, setCopied] = useState<string | null>(null);

  if (balances.isPending || !balances.data) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const currency = group.data?.base_currency ?? "USD";
  const myMemberId = group.data?.my_member_id ?? null;
  const names = new Map(
    balances.data.balances.map((balance) => [balance.member_id, balance.display_name]),
  );
  const groupName = group.data?.name ?? "the group";

  /**
   * The message to send. Three cases, because a settle-up plan routinely suggests a
   * payment between two other people — and telling Evan that "you owe me" when he owes
   * Kaile would be both wrong and annoying.
   */
  const nudgeText = (fromId: number, toId: number, cents: number): string => {
    const amount = formatMoney(cents, currency);
    const from = names.get(fromId) ?? "someone";
    const to = names.get(toId) ?? "someone";
    if (fromId === myMemberId) {
      return `Hey ${to}, I owe you ${amount} from ${groupName}.`;
    }
    if (toId === myMemberId) {
      return `Hey ${from}, you owe me ${amount} from ${groupName}.`;
    }
    return `Hey ${from}, you owe ${to} ${amount} from ${groupName}.`;
  };

  const nudge = (fromId: number, toId: number, cents: number) => {
    void navigator.clipboard?.writeText(nudgeText(fromId, toId, cents));
    setCopied(`${fromId}-${toId}`);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <PageScroll width="medium">
      <div>
        <h1 className="display py-3 text-sm text-muted">Balances</h1>

        <BalanceStrip
          balances={balances.data.balances}
          currency={currency}
          myMemberId={myMemberId}
          className="mb-6"
        />

        <section className="mb-8">
          <h2 className="mb-1 text-2xs uppercase tracking-widest text-muted">Everyone</h2>
          <ul className="divide-y divide-rule overflow-hidden rounded-xl border border-rule bg-raised px-4">
            {balances.data.balances
              .slice()
              .sort((a, b) => b.balance_base_cents - a.balance_base_cents)
              .map((balance) => (
                <li key={balance.member_id} className="flex items-center justify-between py-3">
                  <span>
                    {balance.display_name}
                    {balance.member_id === myMemberId ? (
                      <span className="text-muted"> (you)</span>
                    ) : null}
                  </span>
                  <span className={cn("num", toneClass(balance.balance_base_cents))}>
                    {formatSigned(balance.balance_base_cents, currency)}
                  </span>
                </li>
              ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-1 text-2xs uppercase tracking-widest text-muted">Settle up</h2>
          {balances.data.settle_up.length === 0 ? (
            <p className="py-3 text-sm text-muted">Everyone is square. Nothing to settle.</p>
          ) : (
            <>
              <p className="mb-2 text-xs text-muted">
                The fewest payments that zero everyone out.
              </p>
              <ul className="divide-y divide-rule overflow-hidden rounded-xl border border-rule bg-raised px-4">
                {balances.data.settle_up.map((payment) => (
                  <li
                    key={`${payment.from_member_id}-${payment.to_member_id}`}
                    className="flex flex-wrap items-center gap-2 py-3"
                  >
                    <span className="min-w-[10rem] flex-1 text-sm">
                      <span className="font-medium">
                        {names.get(payment.from_member_id)}
                        {payment.from_member_id === myMemberId ? " (you)" : ""}
                      </span>
                      <span className="text-muted"> pays </span>
                      <span className="font-medium">
                        {names.get(payment.to_member_id)}
                        {payment.to_member_id === myMemberId ? " (you)" : ""}
                      </span>
                    </span>
                    <span className="num">
                      {formatMoney(payment.amount_base_cents, currency)}
                    </span>
                    <Button
                      size="sm"
                      onClick={() =>
                        nudge(
                          payment.from_member_id,
                          payment.to_member_id,
                          payment.amount_base_cents,
                        )
                      }
                    >
                      {copied === `${payment.from_member_id}-${payment.to_member_id}`
                        ? "Copied"
                        : "Nudge"}
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        navigate(
                          `/g/${groupId}/new?type=payment&from=${payment.from_member_id}` +
                            `&to=${payment.to_member_id}&amount=${payment.amount_base_cents}`,
                        )
                      }
                    >
                      Record
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </PageScroll>
  );
}
